import {
  ChevronDown,
  Loader2,
  Pencil,
  Plus,
} from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '@/components/ui/catalyst/button';
import {
  useGetProviderDirectoryLocationQuery,
  useGetProviderDirectoryLocationServicesQuery,
  useGetProviderDirectoryServiceQuery,
} from '@/services/api/providerDirectoryV2Service';

import { ConfigureRequestPlanModal } from '../ConfigureRequestPlanModal';
import { DedupeMergeModal } from '../DedupeMergeModal';
import { EditContactsModal } from '../EditContactsModal';
import { EditLocationModal } from '../EditLocationModal';
import { EditOrganizationModal } from '../EditOrganizationModal';
import { EditRoutingModal } from '../EditRoutingModal';
import { EditServiceModal } from '../EditServiceModal';
import { OrgLinkModal } from '../OrgLinkModal';
import { RequestTemplateModal } from '../RequestTemplateModal';
import { ActivitySection } from './components/ActivitySection';
import { ContactMethodCard } from './components/ContactMethodCard';
import { ProviderHero } from './components/ProviderHero';
import { RelatedProviders } from './components/RelatedProviders';

// ── Modal keys ──────────────────────────────────────────────────────────────
type ModalKey =
  | 'editContacts'
  | 'editService'
  | 'editLocation'
  | 'editOrganization'
  | 'editRouting'
  | 'requestTemplate'
  | 'orgLink'
  | 'dedupeMerge'
  | 'configureRequestPlan';

export const ProviderProfile = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [openModal, setOpenModal] = useState<ModalKey | null>(null);

  // ── Data fetching ───────────────────────────────────────────────────────
  const {
    data: service,
    isLoading: serviceLoading,
    isError: serviceError,
  } = useGetProviderDirectoryServiceQuery(
    { serviceId: serviceId ?? '' },
    { skip: !serviceId },
  );

  const locationId = service?.clinical_location?.id;
  const organizationId = service?.clinical_location?.organization?.id;
  const organizationName = service?.clinical_location?.organization?.name;

  const { data: locationServicesData } =
    useGetProviderDirectoryLocationServicesQuery(
      { locationId: locationId ?? '', limit: 20 },
      { skip: !locationId },
    );

  const { data: locationData } = useGetProviderDirectoryLocationQuery(
    { locationId: locationId ?? '' },
    { skip: !locationId },
  );

  // ── Loading / Error states ──────────────────────────────────────────────
  if (!serviceId) {
    return (
      <div className="flex items-center justify-center py-24 text-info-light">
        <p className="text-sm font-medium">No provider selected</p>
      </div>
    );
  }

  if (serviceLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (serviceError || !service) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-info-light gap-2">
        <p className="text-sm font-medium">Unable to load provider details</p>
        <p className="text-xs font-medium">
          The provider may have been removed or you may not have access.
        </p>
      </div>
    );
  }

  // ── Merge contact points from service + location ────────────────────────
  const contactPoints = [
    ...(service.contact_points ?? []),
    ...(locationData?.contact_points ?? []),
  ];

  // Deduplicate by id
  const seenIds = new Set<string>();
  const uniqueContacts = contactPoints.filter((cp) => {
    if (seenIds.has(cp.id)) return false;
    seenIds.add(cp.id);
    return true;
  });

  const closeModal = () => setOpenModal(null);

  return (
    <div className="flex flex-col gap-8 pb-12">
      {/* Hero */}
      <ProviderHero
        service={service}
        onEditProvider={() => setOpenModal('editService')}
      />

      {/* ── Contact Points ─────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.05em] text-zinc-500">
            Contact Points
          </h2>
          <button
            type="button"
            onClick={() => setOpenModal('editContacts')}
            className="inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            <Plus size={14} />
            Add contact
          </button>
        </div>

        {uniqueContacts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {uniqueContacts.map((contact) => (
              <ContactMethodCard
                key={contact.id}
                contact={contact}
                onEdit={() => setOpenModal('editContacts')}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-background-gray p-6 text-center">
            <p className="text-sm font-medium text-info-light mb-3">
              No contact methods recorded yet
            </p>
            <Button
              color="dark/white"
              onClick={() => setOpenModal('editContacts')}
            >
              <Pencil size={14} />
              Add contacts
            </Button>
          </div>
        )}
      </div>

      {/* ── Related Providers ──────────────────────────────────────────── */}
      {locationServicesData?.results && (
        <RelatedProviders
          services={locationServicesData.results}
          currentServiceId={serviceId}
          organizationName={organizationName}
        />
      )}

      {/* ── Activity ───────────────────────────────────────────────────── */}
      <ActivitySection
        service={service}
        onConfigureRequestPlan={
          locationId
            ? () => setOpenModal('configureRequestPlan')
            : undefined
        }
      />

      {/* ── Admin & Configuration — collapsed inline ───────────────────── */}
      <div className="rounded-xl border border-border bg-background px-5 py-4 flex items-center gap-2.5">
        <ChevronDown size={16} className="shrink-0 text-zinc-400" />
        <span className="text-sm font-semibold text-info-dark">
          Admin & Configuration
        </span>
        <div className="flex items-center gap-1 text-xs text-zinc-500">
          <button
            type="button"
            onClick={() => setOpenModal('editRouting')}
            className="hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            Routing rules
          </button>
          <span>·</span>
          <button
            type="button"
            onClick={() => setOpenModal('requestTemplate')}
            className="hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            Request templates
          </button>
          <span>·</span>
          <button
            type="button"
            onClick={() => setOpenModal('orgLink')}
            className="hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            Org linking
          </button>
          <span>·</span>
          <button
            type="button"
            onClick={() => setOpenModal('dedupeMerge')}
            className="hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            Merge
          </button>
        </div>
      </div>

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <EditContactsModal
        open={openModal === 'editContacts'}
        onClose={closeModal}
        serviceId={serviceId}
      />

      <EditServiceModal
        open={openModal === 'editService'}
        onClose={closeModal}
        serviceId={serviceId}
      />

      {locationId && (
        <EditLocationModal
          open={openModal === 'editLocation'}
          onClose={closeModal}
          locationId={locationId}
        />
      )}

      {organizationId && (
        <EditOrganizationModal
          open={openModal === 'editOrganization'}
          onClose={closeModal}
          organizationId={organizationId}
        />
      )}

      <EditRoutingModal
        open={openModal === 'editRouting'}
        onClose={closeModal}
        serviceId={serviceId}
      />

      <RequestTemplateModal
        open={openModal === 'requestTemplate'}
        onClose={closeModal}
        providerServiceId={serviceId}
        providerName={service.name}
      />

      <OrgLinkModal
        open={openModal === 'orgLink'}
        onClose={closeModal}
        serviceId={serviceId}
      />

      <DedupeMergeModal
        open={openModal === 'dedupeMerge'}
        onClose={closeModal}
        serviceId={serviceId}
      />

      {locationId && (
        <ConfigureRequestPlanModal
          open={openModal === 'configureRequestPlan'}
          onClose={closeModal}
          locationId={locationId}
          locationName={service.clinical_location?.name ?? 'Location'}
        />
      )}
    </div>
  );
};
